import { EXPIRED_CAMPAIGN_PAGE_IDS } from '@/constants/CategoryPageConstants'
import { COLLECTION_ROUTE_NAME } from '@/constants/CollectionPageConstants'
import Query, { DEFAULT_PAGE_SIZE } from '@/modules/category/Query'
import { translate } from '@/plugins/i18n.js'
import SiteConfig from '@/services/SiteConfig'
import { defineStore } from 'pinia'

const VICAMPO_VENDOR_ID = '452'

let debounce
let activeFilters = {}
let queryStash

const NO_SEARCH = 'pagination_page=1'

export const useSearchConfig = defineStore('searchConfig', {
  state: () => ({
    products: [],
    config: {
      category: {
        sort_options: [],
        attributes: [],
        is_enabled: true
      },
      search: '',
      results: [],
      query: {},
      filters: {},
      totalCount: 0,
      totalPages: 0,
      error: false
    },
    loading: false,
    displaySolrData: false,
    categoryIdOverride: null
  }),
  getters: {
    activeAttributes: ({ config: { filters } }) =>
      Object.values(filters)
        .map((filter) => Object.values(filter))
        .flat(),
    activeFilters: ({ config: { filters } }) => Object.keys(filters),
    hasSolrData: ({ displaySolrData }) => displaySolrData,
    categoryIsLoading: ({ loading }) => loading,
    category: ({ config: { category } }) => category,
    getConfig: ({ config }) => config,
    getProducts: ({ products }) => products,
    enableCms: ({ config: { category } }) => category.enable_cms,
    manufacturers: ({ config: { results } }) => {
      const entities =
        results?.filter(({ entity_type: et }) => et === 'manufacturer') ?? []
      // priority if: only 1 manufacturer is present, first in the results (-> the highest rank in score: tested)
      const priority = entities[0] === results?.[0]
      return { entities, priority }
    },
    filterCount: ({ config: { filters } }) =>
      Object.values(filters).reduce(
        (c, filter) => c + Object.keys(filter).length,
        0
      ),
    blocks: ({ config: { category } }) => ({
      before: { html: category.before_html, css: category.before_html_css },
      after: { html: category.after_html, css: category.after_html_css }
    }),
    elbwalkerContext({ config: { category, totalCount } }) {
      const currentPage =
        +this.router.currentRoute.value.query.pagination_page || 1
      const context = [
        'shopping:discover',
        `product_sort:${category.sort}`,
        `product_count:${totalCount}`,
        `product_page:${currentPage}`
      ]
      let filterCount = 0
      Object.entries(this.getConfig.filters).forEach(([attribute, options]) => {
        context.push(`filter_${attribute}:${Object.keys(options).join(',')}`)
        filterCount += Object.keys(options).length
      })
      context.push(`filter_count:${filterCount}`)
      return context
    }
  },
  actions: {
    async fetchManufacturerProducts(manufacturerId) {
      this.config.error = false
      const { ApiController } = await _getModules()
      const [, filters, search] = await _getUrlData(this.router.currentRoute)
      activeFilters = filters

      try {
        this.loading = true
        const res = await ApiController.fetchManufacturerProducts(
          manufacturerId,
          search || NO_SEARCH
        )
        res.data.category.attributes = res.data.category.attributes.map(
          (attr) => ({ ...attr, is_top_filter: true })
        )

        this.config = _getConfig(res, res)

        void this.fetchProducts()
      } catch (e) {
        this.config.error = true
        console.error('Search config fetch failed:', e)
      }
      this.loading = false
    },
    async fetchCategory(categoryIdOverride = null) {
      if (categoryIdOverride) this.categoryIdOverride = categoryIdOverride
      this.config.error = false
      const { ApiController, TrackingHelper } = await _getModules()
      const route = this.router.currentRoute.value

      if (route.name === 'manufacturer') {
        await this.fetchManufacturerProducts(route.params.id)
        return
      }

      const [routeCategoryId, filters, search] = await _getUrlData(
        this.router.currentRoute
      )
      const categoryId = this.categoryIdOverride ?? routeCategoryId
      activeFilters = filters
      const cacheKey = search || NO_SEARCH

      try {
        this.loading = true

        const completedSearch = categoryId
          ? `category_id=${categoryId}&${search}`
          : search
        const res = await ApiController.fetchCategory(completedSearch)

        const redirect = await _safetyCheck(res)

        if (redirect) {
          await this.router.replace(redirect)
          return
        }

        this.config = _getConfig(res, cacheKey)
        void TrackingHelper.sendUtmParams()
        TrackingHelper.sendCategoryPageView(this.category.code)

        void this.fetchProducts()
      } catch (e) {
        this.config.error = true
        console.error('Category fetch failed:', e)
      }
      this.loading = false
    },
    async fetchProducts() {
      const { MetaProduct } = await _getModules()

      this.products =
        this.config.results
          ?.filter((product) => product.entity_type === 'product')
          .map(
            ({ product_id, campaign_id, score_info }) =>
              new MetaProduct({
                product_id,
                campaign_id,
                score_info
              })
          ) ?? []
    },
    updateCategory({ group, value, checked = undefined, immediate = true }) {
      _setupQuery(checked, group, value, this.router)

      if (!immediate) return

      clearTimeout(debounce)
      // boomer timer <3
      debounce = setTimeout(() => {
        void this.applyFilter()
        debounce = null
      }, 500)
    },
    resetFilter(group = null) {
      _clearQuery(group)
    },
    async fetchCollectionPageProducts(collectionPageId) {
      this.config.error = false
      this.loading = true
      try {
        const { ApiController, useCollectionPageStore } = await _getModules()

        const [, filters] = await _getUrlData(this.router.currentRoute)
        activeFilters = filters

        const query = new Query(this.router.currentRoute.value)
        const params = query.toV2Params()

        const {
          collection_page,
          products,
          total,
          size,
          hero_product,
          attributes: rawAttributes = []
        } = await ApiController.fetchCollectionPageProducts(
          collectionPageId,
          params
        )
        const attributes = rawAttributes.map((attr) => ({
          type: 'multi-selection',
          ...attr,
          is_top_filter: true,
          options:
            attr.code === 'vendor'
              ? attr.options.map(({ name, count }) => ({
                  name: String(name),
                  label:
                    String(name) === VICAMPO_VENDOR_ID
                      ? 'VICAMPO'
                      : 'Marketplace',
                  results: count
                }))
              : attr.options.map(({ count, ...opt }) => ({
                  ...opt,
                  results: count
                }))
        }))

        const rawSortOptions = (collection_page.sort_options ?? []).flatMap(
          (name) =>
            name === 'price'
              ? [
                  { value: 'price', label: translate('sort_option_price') },
                  {
                    value: 'price_desc',
                    label: translate('sort_option_price_desc')
                  }
                ]
              : [{ value: name, label: translate('sort_option_' + name) }]
        )
        const activeSort = collection_page.default_sort ?? 'sort_priority'
        const sort_options =
          rawSortOptions.length > 0
            ? rawSortOptions
            : [
                {
                  value: activeSort,
                  label: translate('sort_option_' + activeSort)
                }
              ]

        const hero_data = hero_product
          ? {
              product_id: hero_product.product_id,
              campaign_id: hero_product.campaign_id,
              catch_phrase: collection_page.hero_catch_phrase
            }
          : null

        this.config = {
          category: {
            ...collection_page,
            sort_options,
            hero_data,
            attributes,
            is_enabled: true,
            code: collection_page.page_url ?? String(collectionPageId)
          },
          results: products.map((p) => ({ ...p, entity_type: 'product' })),
          filters: attributes.reduce(_activeFilterReducer, {}),
          totalCount: total,
          totalPages: Math.ceil(total / (size || DEFAULT_PAGE_SIZE)),
          search: params,
          query: {},
          error: false
        }

        const collectionPageStore = useCollectionPageStore()
        collectionPageStore.setFromProductsSearch(collection_page)

        void this.fetchProducts()
      } catch (e) {
        this.config.error = true
        console.error('Collection page products fetch failed:', e)
        const url = this.router.resolve(this.router.currentRoute.value).fullPath
        this.router.push(`/error?original=${url}`)
      } finally {
        this.loading = false
      }
    },
    async applyFilter() {
      try {
        if (!(queryStash instanceof Query)) {
          queryStash = new Query(this.router.currentRoute.value)
        }
        await this.router.push({ query: queryStash.getSearchParams() })
        if (this.router.currentRoute.value.name === COLLECTION_ROUTE_NAME) {
          void this.fetchCollectionPageProducts(
            this.router.currentRoute.value.params.id
          )
        } else {
          void this.fetchCategory()
        }
      } catch (_) {
        // changing filters back and forth could lead to duplicate navigation error
      }
    },
    resetConfig() {
      this.$reset()
      clearTimeout(debounce)
      debounce = null
      queryStash = null
      activeFilters = {}
    }
  }
})

const _getUrlData = async (currentRoute) => {
  const params = currentRoute.value.query
  const filters = Object.keys(params).reduce(
    (filters, key) => ({
      ...filters,
      [key]: (params[key] + '').split(';')
    }),
    {}
  )

  const search = new URLSearchParams(params).toString().replaceAll('%3B', ';')

  return [currentRoute.value.params.id, filters, search]
}

/**
 * collect filters by clustering them: { attribute: { activeOptionName: { ...activeOption }}}
 */
const _activeFilterReducer = (
  cur,
  { code: group, name: groupLabel, options }
) => {
  const activeAttrs = activeFilters[group] ?? []
  const activeOpts = options.reduce(
    (cur, { name, label }) =>
      activeAttrs.includes(String(name))
        ? { ...cur, [name]: { groupLabel, group, name, label } }
        : cur,
    {}
  )

  return Object.keys(activeOpts).length ? { ...cur, [group]: activeOpts } : cur
}

const _setupQuery = (checked, group, value, router) => {
  if (!(queryStash instanceof Query)) {
    queryStash = new Query(router.currentRoute.value)
  }
  queryStash.setPaginationPage(1)
  if (checked !== undefined) {
    checked
      ? queryStash.setSearchParam(group, value, true)
      : queryStash.removeSearchParam(group, value)
  } else {
    queryStash.setSearchParam(group, value)
  }
}

const _clearQuery = (group) => {
  if (!(queryStash instanceof Query)) {
    queryStash = new Query()
  }
  group ? queryStash.removeSearchParam(group) : queryStash.clearSearchParams()
}

const _getConfig = ({ data, headers }, search) => {
  const totalCount = +headers['x-total-count']
  const totalPages = +headers['x-total-pages']
  const filters = data.category.attributes.reduce(_activeFilterReducer, {})
  const rewrite = data.rewrite
  const error = false

  return { ...data, totalCount, totalPages, search, filters, rewrite, error }
}

const _safetyCheck = async ({ data }) => {
  const redirect = data.redirect_url
  if (redirect) return redirect

  const isEnabled = data?.is_enabled ?? data?.category?.is_enabled
  if (isEnabled) return null

  const { customerStore } = await _getModules()
  await customerStore.waitUntilPending()

  if (customerStore.customerIsAdmin) return null

  const pageId = EXPIRED_CAMPAIGN_PAGE_IDS[SiteConfig.locale]
  if (pageId) {
    return `/kampagne-abgelaufen-cms-p${pageId}`
  }

  return { name: 'home' }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: ApiController },
      { default: TrackingHelper },
      { default: MetaProduct },
      { useCustomerStore },
      { useCollectionPageStore }
    ] = await Promise.all([
      import('@/services/ApiController'),
      import('@/services/helpers/TrackingHelper'),
      import('@/modules/product/MetaProduct'),
      import('@/stores/customer'),
      import('@/stores/collectionPage')
    ])
    const customerStore = useCustomerStore()
    cache = {
      ApiController,
      TrackingHelper,
      MetaProduct,
      customerStore,
      useCollectionPageStore
    }
    return cache
  }
})()
